import { glyphRows } from './pixelFont';
import { ctx2d, mixColor, noise, px } from './sprite';
import { SIGN_EDGE, SIGN_YELLOW } from './theme';

/**
 * The title-screen establishing shot: a Waffle House at night, sign blazing.
 * The logical width follows the viewport's aspect ratio so it composes well
 * on both phones and desktops.
 */
export class ExteriorRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private w = 320;
  private h = 180;
  private t = 0;
  private carX = -40;
  reducedMotion = false;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = ctx2d(canvas);
    this.resize();
  }

  /** Match the element's aspect ratio (or an explicit one for offscreen use). */
  resize(explicitAspect?: number): void {
    const rect = this.canvas.getBoundingClientRect();
    const measured = rect.width > 0 && rect.height > 0 ? rect.width / rect.height : 16 / 9;
    const aspect = explicitAspect ?? measured;
    this.h = 180;
    this.w = Math.max(90, Math.min(420, Math.round(this.h * aspect)));
    this.canvas.width = this.w;
    this.canvas.height = this.h;
    this.ctx.imageSmoothingEnabled = false;
  }

  render(dt: number): void {
    this.t += dt;
    const { ctx, w, h } = this;
    const ground = Math.round(h * 0.74);

    // Sky
    for (let y = 0; y < ground; y++)
      px(ctx, 0, y, w, 1, mixColor('#050817', '#2a1846', y / ground));
    for (let i = 0; i < 120; i++) {
      const sx = Math.floor(noise(i, 1, 77) * w);
      const sy = Math.floor(noise(i, 2, 77) * ground * 0.7);
      if (noise(i, Math.floor(this.t * 1.5), 3) > 0.12) {
        ctx.globalAlpha = 0.35 + noise(i, 3, 77) * 0.65;
        px(ctx, sx, sy, 1, 1, '#ffffff');
      }
    }
    ctx.globalAlpha = 1;
    // Crescent moon: a disc minus an offset disc.
    const moonX = Math.round(w * 0.14);
    const moonY = 20;
    for (let y = -6; y <= 6; y++) {
      for (let x = -6; x <= 6; x++) {
        const inMoon = x * x + y * y <= 30;
        const inShadow = (x - 3) * (x - 3) + (y + 1) * (y + 1) <= 26;
        if (inMoon && !inShadow)
          px(ctx, moonX + x, moonY + y, 1, 1, x < -3 ? '#e9dfb8' : '#f8f1d0');
      }
    }

    // Trees
    for (let x = 0; x < w; x++) {
      const th = 6 + Math.floor(noise(Math.floor(x / 4), 1, 5) * 10);
      px(ctx, x, ground - th, 1, th, '#08110d');
    }

    // Building
    const bw = Math.min(w - 12, 190);
    const bx = Math.round((w - bw) / 2);
    const bh = 38;
    const by = ground - bh;
    // On landscape screens the tall pole sign stands at the edge of the lot;
    // on portrait phones the roofline sign carries the name instead.
    const wideSign = w >= 230;
    this.drawBuilding(bx, by, bw, bh);

    const signX = wideSign ? w - 30 : Math.round(w / 2);
    if (wideSign) this.drawPoleSign(signX, Math.round(ground * 0.4), ground);

    // Parking lot
    px(ctx, 0, ground, w, h - ground, '#17181e');
    for (let x = 4; x < w; x += 22) px(ctx, x, ground + 8, 1, 14, '#9c8430');
    px(ctx, 0, ground, w, 1, '#2b2d36');
    // Puddle reflection of the sign
    const puddleY = ground + 26;
    ctx.globalAlpha = 0.55;
    px(ctx, signX - 18, puddleY, 36, 5, '#0d1a2a');
    for (let i = 0; i < 6; i++) {
      const wob = this.reducedMotion ? 0 : Math.round(Math.sin(this.t * 2 + i) * 1);
      px(ctx, signX - 14 + i * 5 + wob, puddleY + 1 + (i % 2), 4, 1, SIGN_YELLOW);
    }
    ctx.globalAlpha = 1;

    // A car rolls past now and then
    this.carX += dt * 55;
    if (this.carX > w + 60) this.carX = -120 - Math.random() * 200;
    const cy = h - 12;
    px(ctx, this.carX, cy, 26, 5, '#8e1d1d');
    px(ctx, this.carX + 5, cy - 4, 15, 4, '#6b1515');
    px(ctx, this.carX + 7, cy - 3, 5, 2, '#9fb6cc');
    px(ctx, this.carX + 13, cy - 3, 5, 2, '#9fb6cc');
    px(ctx, this.carX + 3, cy + 5, 4, 2, '#0a0a0a');
    px(ctx, this.carX + 19, cy + 5, 4, 2, '#0a0a0a');
    px(ctx, this.carX + 25, cy + 1, 1, 2, '#fff3b0');
    ctx.globalAlpha = 0.2;
    px(ctx, this.carX + 26, cy, 30, 4, '#fff3b0');
    ctx.globalAlpha = 1;
  }

  private drawBuilding(x: number, y: number, w: number, h: number): void {
    const ctx = this.ctx;
    // Roof band
    px(ctx, x - 3, y - 11, w + 6, 11, '#3a2416');
    px(ctx, x - 3, y - 11, w + 6, 1, '#5a3a24');
    const tiles = 'WAFFLE HOUSE';
    const tw = Math.min(6, Math.floor((w + 1) / tiles.length) - 1);
    const total = tiles.length * (tw + 1) - 1;
    if (tw >= 5 && total <= w) {
      const tx0 = x + Math.round((w - total) / 2);
      for (let i = 0; i < tiles.length; i++) {
        const ch = tiles[i] ?? ' ';
        if (ch === ' ') continue;
        const tx = tx0 + i * (tw + 1);
        px(ctx, tx, y - 9, tw, 7, SIGN_YELLOW);
        glyphRows(ch).forEach((row, gy) => {
          for (let gx = 0; gx < row.length; gx++)
            if (row[gx] === '#')
              px(ctx, tx + Math.floor((tw - 3) / 2) + gx, y - 8 + gy, 1, 1, '#141414');
        });
      }
    } else {
      for (let tx = x; tx < x + w - 5; tx += 7) px(ctx, tx, y - 9, 6, 7, SIGN_YELLOW);
    }
    // Walls
    px(ctx, x, y, w, h, '#b98a57');
    for (let yy = y + 2; yy < y + h; yy += 3) px(ctx, x, yy, w, 1, '#a57744');
    // Big warm windows with diners
    const winY = y + 6;
    const winH = 18;
    px(ctx, x + 4, winY, w - 8, winH, '#ffcf73');
    px(ctx, x + 4, winY + winH - 5, w - 8, 5, '#f2b24f');
    for (let wx = x + 4; wx < x + w - 4; wx += 16) px(ctx, wx, winY, 1, winH, '#4a2e1a');
    for (let i = 0; i < Math.floor(w / 14); i++) {
      if (noise(i, 4, 9) < 0.45) continue;
      const sx = x + 8 + i * 14 + Math.floor(noise(i, 5, 9) * 6);
      const bob = this.reducedMotion ? 0 : Math.floor((this.t + i) % 2);
      px(ctx, sx, winY + 8 + bob, 5, 5, '#6a3b20');
      px(ctx, sx - 1, winY + 13 + bob, 7, 5, '#7c4526');
    }
    // Door
    const dx = x + Math.round(w * 0.12);
    px(ctx, dx, y + h - 16, 9, 16, '#3a2416');
    px(ctx, dx + 2, y + h - 14, 5, 7, '#ffe3a3');
    // Glow spilling on the lot
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = 0.25;
    px(ctx, x, y + h, w, 6, '#ffb84d');
    ctx.globalAlpha = 0.12;
    px(ctx, x + 6, y + h + 6, w - 12, 6, '#ffb84d');
    ctx.restore();
  }

  private drawPoleSign(cx: number, top: number, ground: number): void {
    const ctx = this.ctx;
    const tile = 7;
    const gap = 1;
    const rowW = 6 * (tile + gap) - gap;
    const flicker = !this.reducedMotion && noise(Math.floor(this.t * 10), 7, 2) > 0.985;
    // Glow
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const g = ctx.createRadialGradient(cx, top + tile, 4, cx, top + tile, 70);
    g.addColorStop(0, flicker ? 'rgba(255,210,63,0.12)' : 'rgba(255,210,63,0.42)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(cx - 80, top - 60, 160, 160);
    ctx.restore();
    // Pole
    px(ctx, cx - 1, top + 2 * (tile + gap), 3, ground - top, '#23252c');
    px(ctx, cx - rowW / 2 - 2, top - 2, rowW + 4, 2 * (tile + gap) + 3, '#1a1b20');
    const rows = ['WAFFLE', 'HOUSE'];
    rows.forEach((word, r) => {
      const ww = word.length * (tile + gap) - gap;
      const x0 = Math.round(cx - ww / 2);
      for (let i = 0; i < word.length; i++) {
        const tx = x0 + i * (tile + gap);
        const ty = top + r * (tile + gap);
        px(ctx, tx, ty, tile, tile, flicker && i % 2 ? '#8a7424' : SIGN_YELLOW);
        px(ctx, tx, ty + tile - 1, tile, 1, SIGN_EDGE);
        glyphRows(word[i] ?? ' ').forEach((row, gy) => {
          for (let gx = 0; gx < row.length; gx++)
            if (row[gx] === '#') px(ctx, tx + 2 + gx, ty + 1 + gy, 1, 1, '#141414');
        });
      }
    });
  }
}
